import type { HTMLAttributes } from 'react';
import { cn } from '../../lib/cn.js';

export interface UnreadDotProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  count: number;
  /**
   * Screen-reader text — "3 unread". Without it the badge reads as a bare
   * "3" next to the word "Inbox", which says nothing.
   */
  label?: string;
  /** The number it stops counting past. */
  cap?: number;
}

/**
 * A count on a nav row, an inbox row or a tab.
 *
 * Capped at 9+ rather than widened: the number is a prompt to look, and a
 * three-digit one only tells somebody they have already failed. Renders
 * nothing at zero, so a call site can hand it a count unconditionally
 * instead of guarding every one.
 *
 * It lived privately in the partner portal's `AppLayout` until the admin
 * console needed the same thing; two copies of a badge is how the two
 * consoles start disagreeing about what "unread" looks like.
 */
export function UnreadDot({ count, label, cap = 9, className, ...props }: UnreadDotProps) {
  if (count <= 0) return null;

  return (
    <span
      aria-label={label}
      className={cn(
        'flex min-w-5 shrink-0 items-center justify-center rounded-full bg-accent px-1.5',
        'text-[10px] font-bold leading-5 text-accent-text',
        className,
      )}
      {...props}
    >
      {count > cap ? `${cap}+` : count}
    </span>
  );
}
